/**
 * Security utilities for the FPRA Migrants Guide
 * Protects against XSS, injection attacks, and data tampering
 */

// Sanitize user input to prevent XSS attacks
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  // Remove script tags and dangerous attributes
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/<iframe/gi, '')
    .replace(/<object/gi, '')
    .replace(/<embed/gi, '')
    .trim();
};

// Sanitize HTML content
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';
  
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone number (international format)
export const isValidPhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

// Validate URL
export const isValidURL = (url) => {
  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

// Rate limiting for actions (prevent spam)
const rateLimitMap = new Map();

export const rateLimit = (action, maxAttempts = 10, windowMs = 60000) => {
  const now = Date.now();
  const key = action;
  
  if (!rateLimitMap.has(key)) {
    rateLimitMap.set(key, []);
  }
  
  const attempts = rateLimitMap.get(key);
  
  // Remove old attempts outside the window
  const recentAttempts = attempts.filter(time => now - time < windowMs);
  
  if (recentAttempts.length >= maxAttempts) {
    return false; // Rate limit exceeded
  }
  
  recentAttempts.push(now);
  rateLimitMap.set(key, recentAttempts);
  
  return true; // Action allowed
};

// Validate localStorage data structure
export const validateStorageData = (data) => {
  if (!data || typeof data !== 'object') return false;
  
  const requiredFields = ['version', 'preferences'];
  return requiredFields.every(field => field in data);
};

// Sanitize localStorage before saving
export const sanitizeStorageData = (data) => {
  if (!data || typeof data !== 'object') return null;
  
  const sanitized = { ...data };
  
  // Sanitize notes content
  if (sanitized.notes && typeof sanitized.notes === 'object') {
    Object.keys(sanitized.notes).forEach(pageId => {
      if (Array.isArray(sanitized.notes[pageId])) {
        sanitized.notes[pageId] = sanitized.notes[pageId].map(note => ({
          ...note,
          content: sanitizeInput(note.content || '')
        }));
      }
    });
  }
  
  // Sanitize search history
  if (Array.isArray(sanitized.searchHistory)) {
    sanitized.searchHistory = sanitized.searchHistory.map(item => ({
      ...item,
      query: sanitizeInput(item.query || '')
    }));
  }
  
  return sanitized;
};

// Prevent console manipulation in production
export const secureConsole = () => {
  if (process.env.NODE_ENV === 'production') {
    // Disable console in production
    const noop = () => {};
    ['log', 'warn', 'error', 'debug', 'info'].forEach(method => {
      console[method] = noop;
    });
  }
};

// Detect and prevent DevTools (optional, can be aggressive)
export const detectDevTools = () => {
  if (process.env.NODE_ENV === 'production') {
    const threshold = 160;
    const widthThreshold = window.outerWidth - window.innerWidth > threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > threshold;
    
    if (widthThreshold || heightThreshold) {
      console.warn('Developer tools detected');
      // Don't block, just log
    }
  }
};

// Protect against prototype pollution
export const safeObjectMerge = (target, source) => {
  const result = { ...target };
  
  Object.keys(source).forEach(key => {
    // Skip prototype pollution attempts
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return;
    }
    
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = safeObjectMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  });
  
  return result;
};

// Generate secure random IDs
export const generateSecureId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
};

// Validate file uploads (if added in future)
export const validateFile = (file, allowedTypes = ['image/jpeg', 'image/png', 'image/webp'], maxSize = 5 * 1024 * 1024) => {
  if (!file) return { valid: false, error: 'No file provided' };
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'File too large' };
  }
  
  return { valid: true };
};

// Encrypt sensitive data before storing (basic obfuscation)
export const obfuscate = (text) => {
  if (!text) return '';
  return btoa(encodeURIComponent(text));
};

export const deobfuscate = (encoded) => {
  if (!encoded) return '';
  try {
    return decodeURIComponent(atob(encoded));
  } catch {
    return '';
  }
};

// Check if running in secure context (HTTPS)
export const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

// Prevent copy-paste of sensitive info
export const preventCopy = (element) => {
  if (element) {
    element.addEventListener('copy', (e) => {
      e.preventDefault();
      return false;
    });
  }
};

// Log security events (for monitoring)
export const logSecurityEvent = (event, details = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('🔒 Security Event:', event, details);
  }
  
  // In production, you could send to analytics
  // analytics.track('security_event', { event, ...details });
};

export default {
  sanitizeInput,
  sanitizeHTML,
  isValidEmail,
  isValidPhone,
  isValidURL,
  rateLimit,
  validateStorageData,
  sanitizeStorageData,
  secureConsole,
  detectDevTools,
  safeObjectMerge,
  generateSecureId,
  validateFile,
  obfuscate,
  deobfuscate,
  isSecureContext,
  preventCopy,
  logSecurityEvent
};
